/**
 * 同步 ant-design-vue 1.x 固定列与内容列：
 * 1) 行高强制一致（内容列多行换行时操作列不再错位）
 * 2) 表宽小于容器时，右固定列贴齐表右缘，消除中间空白
 */

const SYNC_FLAG = "data-erp-fixed-synced"
const OBSERVE_FLAG = "data-erp-fixed-observe"
const ROW_OBSERVE_FLAG = "data-erp-row-observe"

let installed = false
let mo = null
let ro = null
let rowRo = null
let scheduleTimer = null
const pendingContainers = new Set()

function isVisible(el) {
  if (!el || !el.getBoundingClientRect) return false
  const rect = el.getBoundingClientRect()
  return rect.width > 0 && rect.height > 0
}

function collectBodyRows(tableRoot) {
  const scrollBody = tableRoot.querySelector(
    ".ant-table-scroll .ant-table-body"
  )
  const body =
    scrollBody ||
    tableRoot.querySelector(".ant-table-content > .ant-table-body") ||
    tableRoot.querySelector(".ant-table-body")
  if (!body) return []
  if (body.closest(".ant-table-fixed-left, .ant-table-fixed-right")) {
    return []
  }
  return Array.prototype.slice.call(
    body.querySelectorAll("table > tbody > tr.ant-table-row")
  )
}

function collectFixedRows(tableRoot, side) {
  const fixed = tableRoot.querySelector(`.ant-table-fixed-${side}`)
  if (!fixed) return []
  const body =
    fixed.querySelector(".ant-table-body-inner") ||
    fixed.querySelector(".ant-table-body")
  if (!body) return []
  return Array.prototype.slice.call(
    body.querySelectorAll("table > tbody > tr.ant-table-row")
  )
}

function measureRowHeight(row) {
  if (!row) return 0
  const oh = row.offsetHeight
  if (oh > 0) return oh
  const rect = row.getBoundingClientRect()
  return rect && rect.height ? Math.ceil(rect.height) : 0
}

function clearInlineHeight(row) {
  if (!row) return
  row.style.removeProperty("height")
  row.style.removeProperty("max-height")
  row.style.removeProperty("min-height")
  const cells = row.children
  for (let i = 0; i < cells.length; i++) {
    const td = cells[i]
    if (!td || td.nodeType !== 1) continue
    td.style.removeProperty("height")
    td.style.removeProperty("max-height")
    td.style.removeProperty("min-height")
  }
  row.removeAttribute(SYNC_FLAG)
}

/**
 * 强制写入行高（内容列 + 操作列都写），保证两侧完全一致
 */
function forceRowHeight(row, height) {
  if (!row || !(height > 0)) return
  const px = `${height}px`
  row.style.setProperty("height", px, "important")
  row.style.setProperty("min-height", px, "important")
  const cells = row.children
  for (let i = 0; i < cells.length; i++) {
    const td = cells[i]
    if (!td || td.nodeType !== 1) continue
    td.style.setProperty("height", px, "important")
    td.style.setProperty("min-height", px, "important")
  }
  row.setAttribute(SYNC_FLAG, px)
}

function syncHeaderRowHeights(tableRoot) {
  const scrollHeader =
    tableRoot.querySelector(".ant-table-scroll .ant-table-header") ||
    tableRoot.querySelector(".ant-table-header")
  if (!scrollHeader) return
  const mainRows = Array.prototype.slice.call(
    scrollHeader.querySelectorAll(".ant-table-thead > tr")
  )
  if (!mainRows.length) return

  ;["left", "right"].forEach(side => {
    const fixed = tableRoot.querySelector(`.ant-table-fixed-${side}`)
    if (!fixed) return
    const fixedHeader = fixed.querySelector(".ant-table-header") || fixed
    const fixedRows = Array.prototype.slice.call(
      fixedHeader.querySelectorAll(".ant-table-thead > tr")
    )
    const len = Math.min(mainRows.length, fixedRows.length)
    for (let i = 0; i < len; i++) {
      clearInlineHeight(mainRows[i])
      clearInlineHeight(fixedRows[i])
      const h = Math.max(
        measureRowHeight(mainRows[i]),
        measureRowHeight(fixedRows[i])
      )
      if (h > 0) {
        forceRowHeight(mainRows[i], h)
        forceRowHeight(fixedRows[i], h)
      }
    }
  })
}

function syncBodyScrollTop(tableRoot) {
  const mainBody =
    tableRoot.querySelector(".ant-table-scroll .ant-table-body") ||
    tableRoot.querySelector(".ant-table-body")
  if (
    !mainBody ||
    mainBody.closest(".ant-table-fixed-left, .ant-table-fixed-right")
  ) {
    return
  }
  const top = mainBody.scrollTop
  ;["left", "right"].forEach(side => {
    const fixed = tableRoot.querySelector(`.ant-table-fixed-${side}`)
    if (!fixed) return
    const fixedBody =
      fixed.querySelector(".ant-table-body-inner") ||
      fixed.querySelector(".ant-table-body")
    if (fixedBody && fixedBody.scrollTop !== top) {
      fixedBody.scrollTop = top
    }
  })
}

/**
 * 若 scroll.x 被设得过大，主表会被撑出「列宽之和」以外的空白；
 * 把主表宽度收到 colgroup 合计，避免操作列前出现空洞。
 */
function shrinkInflatedScrollTable(tableRoot) {
  const scroll = tableRoot.querySelector(".ant-table-scroll")
  if (!scroll) return 0

  const colgroup = scroll.querySelector("colgroup")
  if (!colgroup || !colgroup.children || !colgroup.children.length) return 0

  let sum = 0
  for (let i = 0; i < colgroup.children.length; i++) {
    const col = colgroup.children[i]
    const raw = (col.style && col.style.width) || col.getAttribute("width") || ""
    const w = parseInt(raw, 10)
    // ant-design-vue 会在首轮渲染时保留一部分未定宽的 col。
    // 此时若只累加选择列/固定操作列，会把整张主表误缩到两列宽度，
    // 数据行虽然存在，但所有内容列都会被挤出可视区。
    if (Number.isNaN(w) || w <= 0) return 0
    sum += w
  }
  if (sum <= 0) return 0

  const tables = scroll.querySelectorAll("table")
  tables.forEach(t => {
    const cur = t.offsetWidth || 0
    if (cur > sum + 4) {
      t.style.setProperty("width", `${sum}px`, "important")
      t.style.setProperty("min-width", `${sum}px`, "important")
    }
  })
  return sum
}

/**
 * 消除内容区与右固定操作列之间的空白：
 * 按主表「操作」列实际右缘贴齐 fixed-right，避免中间留白。
 */
function syncFixedRightFlush(tableRoot) {
  const content = tableRoot.querySelector(".ant-table-content")
  const fixedRight = tableRoot.querySelector(".ant-table-fixed-right")
  const scroll = tableRoot.querySelector(".ant-table-scroll")
  if (!content || !fixedRight || !scroll) return

  shrinkInflatedScrollTable(tableRoot)

  const contentRect = content.getBoundingClientRect()
  if (!(contentRect.width > 0)) return

  const mainBody = scroll.querySelector(".ant-table-body")
  const sb =
    mainBody && mainBody.offsetWidth > mainBody.clientWidth
      ? mainBody.offsetWidth - mainBody.clientWidth
      : 0

  // 仍需横向滚动时，固定列贴容器右侧；仅在无横滚（表窄于容器）时贴齐操作列去空白
  const needScrollX =
    mainBody && mainBody.scrollWidth > mainBody.clientWidth + 2

  let right = sb
  if (!needScrollX) {
    const opCell =
      scroll.querySelector("th.erp-table-operation-col") ||
      scroll.querySelector("td.erp-table-operation-col") ||
      scroll.querySelector(
        ".ant-table-thead > tr > th:last-child:not(.ant-table-selection-column)"
      )

    if (opCell) {
      const opRect = opCell.getBoundingClientRect()
      right = Math.max(0, Math.round(contentRect.right - opRect.right))
    } else {
      const scrollTable =
        scroll.querySelector(".ant-table-body > table") ||
        scroll.querySelector(".ant-table-header > table") ||
        scroll.querySelector("table")
      if (scrollTable) {
        const tableW = Math.max(
          scrollTable.offsetWidth || 0,
          scrollTable.scrollWidth || 0
        )
        const contentW = content.clientWidth
        if (tableW + 1 < contentW) {
          right = Math.max(0, contentW - tableW - sb)
        }
      }
    }
  }

  fixedRight.style.setProperty("right", `${right}px`, "important")
  if (right > 1) {
    fixedRight.classList.add("erp-fixed-right-flushed")
  } else {
    fixedRight.classList.remove("erp-fixed-right-flushed")
  }
}

/**
 * 按行取内容列与固定列自然高度的最大值，强制写入两侧
 */
function syncBodyRowHeights(tableRoot, bodyRows) {
  const leftRows = collectFixedRows(tableRoot, "left")
  const rightRows = collectFixedRows(tableRoot, "right")
  const len = bodyRows.length

  // 先清高度，按自然内容测量
  for (let i = 0; i < len; i++) {
    clearInlineHeight(bodyRows[i])
    if (leftRows[i]) clearInlineHeight(leftRows[i])
    if (rightRows[i]) clearInlineHeight(rightRows[i])
  }
  void (bodyRows[0] && bodyRows[0].offsetHeight)

  for (let i = 0; i < len; i++) {
    const bodyH = measureRowHeight(bodyRows[i])
    const leftH = leftRows[i] ? measureRowHeight(leftRows[i]) : 0
    const rightH = rightRows[i] ? measureRowHeight(rightRows[i]) : 0
    const h = Math.max(bodyH, leftH, rightH)
    if (!(h > 0)) continue

    forceRowHeight(bodyRows[i], h)
    if (leftRows[i]) forceRowHeight(leftRows[i], h)
    if (rightRows[i]) forceRowHeight(rightRows[i], h)
  }
}

function observeBodyRows(bodyRows) {
  if (!rowRo || !bodyRows || !bodyRows.length) return
  bodyRows.forEach(row => {
    if (!row || row.getAttribute(ROW_OBSERVE_FLAG) === "1") return
    row.setAttribute(ROW_OBSERVE_FLAG, "1")
    try {
      rowRo.observe(row)
    } catch (e) {
      /* ignore */
    }
  })
}

function observeTableBody(tableRoot) {
  if (typeof ResizeObserver === "undefined" || !ro) return
  const scrollBody =
    tableRoot.querySelector(".ant-table-scroll .ant-table-body") ||
    tableRoot.querySelector(".ant-table-body")
  if (!scrollBody || scrollBody.getAttribute(OBSERVE_FLAG) === "1") return
  if (scrollBody.closest(".ant-table-fixed-left, .ant-table-fixed-right")) {
    return
  }
  scrollBody.setAttribute(OBSERVE_FLAG, "1")
  try {
    ro.observe(scrollBody)
  } catch (e) {
    /* ignore */
  }
  // 同时观察表格根，容器变宽时重算固定列贴边
  try {
    ro.observe(tableRoot)
  } catch (e2) {
    /* ignore */
  }
}

/**
 * 对单个 .ant-table 根节点同步固定列
 */
export function syncOneTableFixedRowHeights(tableRoot) {
  if (!tableRoot || !tableRoot.querySelector) return
  if (!isVisible(tableRoot)) return

  const hasFixed =
    tableRoot.querySelector(".ant-table-fixed-left") ||
    tableRoot.querySelector(".ant-table-fixed-right")
  if (!hasFixed) return

  syncFixedRightFlush(tableRoot)
  syncHeaderRowHeights(tableRoot)

  let bodyRows = collectBodyRows(tableRoot)
  if (!bodyRows.length) {
    const fallbackBody = tableRoot.querySelector(
      ".ant-table-content > .ant-table-scroll .ant-table-body, .ant-table-body"
    )
    if (!fallbackBody) return
    bodyRows = Array.prototype.slice
      .call(fallbackBody.querySelectorAll("table > tbody > tr.ant-table-row"))
      .filter(r => !r.closest(".ant-table-fixed-left, .ant-table-fixed-right"))
  }
  if (!bodyRows.length) return

  syncBodyRowHeights(tableRoot, bodyRows)
  syncBodyScrollTop(tableRoot)
  observeTableBody(tableRoot)
  observeBodyRows(bodyRows)
}

/**
 * 同步页面内（或指定容器内）所有表格固定列
 */
export function syncAntTableFixedRowHeights(container) {
  const root =
    container && container.querySelectorAll ? container : document
  const tables = root.querySelectorAll(".ant-table")
  tables.forEach(syncOneTableFixedRowHeights)
}

function dispatchWindowResize() {
  if (typeof window === "undefined") return
  try {
    window.dispatchEvent(new Event("resize"))
  } catch (e) {
    const evt = document.createEvent("Event")
    evt.initEvent("resize", true, true)
    window.dispatchEvent(evt)
  }
}

export function triggerTableFixedAlign(container, options = {}) {
  const root = container || document
  const withResize = options.withResize !== false
  syncAntTableFixedRowHeights(root)
  if (withResize) {
    dispatchWindowResize()
    if (typeof requestAnimationFrame === "function") {
      requestAnimationFrame(() => {
        syncAntTableFixedRowHeights(root)
      })
    }
  }
}

export function scheduleTableFixedAlign(
  container,
  times = [0, 50, 120, 280, 500, 900]
) {
  const root = container || document
  const delays = times && times.length ? times : [0]
  delays.forEach(delay => {
    const run = () => {
      triggerTableFixedAlign(root, { withResize: delay === 0 })
    }
    if (delay <= 0) {
      if (typeof requestAnimationFrame === "function") {
        requestAnimationFrame(() => requestAnimationFrame(run))
      } else {
        setTimeout(run, 0)
      }
    } else {
      setTimeout(run, delay)
    }
  })
}

function queueSync(container) {
  pendingContainers.add(container || document)
  if (scheduleTimer) return
  scheduleTimer = setTimeout(() => {
    scheduleTimer = null
    const list = Array.from(pendingContainers)
    pendingContainers.clear()
    list.forEach(c => {
      syncAntTableFixedRowHeights(c)
    })
  }, 48)
}

/**
 * 全站安装：监听表格 DOM / 行尺寸变化，自动对齐
 */
export function installTableFixedRowSync() {
  if (
    installed ||
    typeof window === "undefined" ||
    typeof document === "undefined"
  ) {
    return
  }
  installed = true

  if (typeof ResizeObserver !== "undefined") {
    ro = new ResizeObserver(() => {
      queueSync(document)
    })
    rowRo = new ResizeObserver(() => {
      queueSync(document)
    })
  }

  if (typeof MutationObserver !== "undefined") {
    mo = new MutationObserver(mutations => {
      let need = false
      for (let i = 0; i < mutations.length; i++) {
        const m = mutations[i]
        if (m.type === "childList") {
          const nodes = []
          if (m.addedNodes && m.addedNodes.length) {
            for (let j = 0; j < m.addedNodes.length; j++)
              nodes.push(m.addedNodes[j])
          }
          if (m.target && m.target.classList) nodes.push(m.target)
          for (let j = 0; j < nodes.length; j++) {
            const n = nodes[j]
            if (!n || n.nodeType !== 1) continue
            if (
              (n.classList &&
                (n.classList.contains("ant-table") ||
                  n.classList.contains("ant-table-row") ||
                  n.classList.contains("ant-spin-container"))) ||
              (n.querySelector &&
                n.querySelector(
                  ".ant-table, .ant-table-row, .ant-table-fixed-right"
                ))
            ) {
              need = true
              break
            }
          }
          if (need) break
        } else if (m.type === "attributes") {
          const t = m.target
          if (
            t &&
            t.classList &&
            (t.classList.contains("ant-spin-spinning") ||
              t.classList.contains("ant-spin-nested-loading") ||
              t.classList.contains("ant-table"))
          ) {
            need = true
            break
          }
        }
      }
      if (need) queueSync(document)
    })
    mo.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class"]
    })
  }

  scheduleTableFixedAlign(document)
}

export function uninstallTableFixedRowSync() {
  if (mo) {
    mo.disconnect()
    mo = null
  }
  if (ro) {
    ro.disconnect()
    ro = null
  }
  if (rowRo) {
    rowRo.disconnect()
    rowRo = null
  }
  if (scheduleTimer) {
    clearTimeout(scheduleTimer)
    scheduleTimer = null
  }
  pendingContainers.clear()
  installed = false
}

/** @deprecated 兼容旧调用名 */
export function installTableFixedAlignObserver() {
  installTableFixedRowSync()
  return uninstallTableFixedRowSync
}
